<template>
  <div class="flex items-center gap-4">
    <DateRangePicker
      :start-date="startDate"
      :end-date="endDate"
      :error-message="savedSearchStore.dateRangeErrMsg"
      @update="(newDateRange) => $emit('update-date-range', newDateRange)"
      @on-popover-close="onPopoverClose"
    />
    <ClientOnly v-if="hasCustomButton">
      <PrimaryButton variant="secondary" @click="$emit('custom-action')">
        <span class="flex justify-center items-center gap-2">
          <font-awesome-icon class="text-base" :icon="customButtonIcon" />
          <span>{{ customButtonText }}</span>
        </span>
      </PrimaryButton>
    </ClientOnly>
    <AppliedFilters
      :search-save-disabled="saveButtonDisabled"
      @handle-save="$emit('handle-save', (filters = true))"
    />
    <AiSummaryButton
      v-if="hasAiSummaryButton"
      :disabled="aiSummaryDisabled"
      @click="$emit('ai-summary-click')"
    />
    <ClientOnly v-if="hasShowChartsButton">
      <PrimaryButton variant="secondary">
        <span class="flex justify-center items-center gap-1">
          <ToggleSwitch
            :switch-value="showChartsValue"
            @switch-clicked="$emit('update-show-charts')"
          />
          <span class="font-body-1 text-accent-700">{{
            t("mentions.showCharts")
          }}</span>
        </span>
      </PrimaryButton>
    </ClientOnly>
    <PrimaryButton
      v-if="hasSaveButton"
      :disabled="saveButtonDisabled"
      @on-click="$emit('handle-save', (filters = false))"
    >
      <p>{{ t("common.save") }}</p>
    </PrimaryButton>
    <HamburgerButton
      v-if="hasHamburgerMenu"
      :current-route="savedSearchStore.currentRoute"
    >
      <slot />
    </HamburgerButton>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import DateRangePicker from "~/components/shared/DateRangePicker.vue";
import AppliedFilters from "~/components/filters/AppliedFilters.vue";
import PrimaryButton from "~/components/shared/primary-button/PrimaryButton.vue";
import ToggleSwitch from "@/components/shared/toggle-switch/ToggleSwitch.vue";
import HamburgerButton from "~/components/shared/HamburgerButton.vue";
import AiSummaryButton from "~/components/shared/AiSummaryButton.vue";
import { retrieveSessionDateRange } from "~/utils/RetrieveSessionDateRange";
import { useSearchStore } from "~/store/search";

const { t } = useI18n();

const savedSearchStore = useSearchStore();
let filters: boolean;

defineProps({
  startDate: {
    type: Date,
    required: true,
  },
  endDate: {
    type: Date,
    required: true,
  },
  hasShowChartsButton: {
    type: Boolean,
    default: false,
  },
  showChartsValue: {
    type: Boolean,
    default: false,
  },
  hasSaveButton: {
    type: Boolean,
    default: false,
  },
  saveButtonDisabled: {
    type: Boolean,
    default: false,
  },
  hasHamburgerMenu: {
    type: Boolean,
    default: false,
  },
  hasCustomButton: {
    type: Boolean,
    default: false,
  },
  customButtonText: {
    type: String,
    default: "",
  },
  customButtonIcon: {
    type: Array<String>,
    default: () => [],
  },
  hasAiSummaryButton: {
    type: Boolean,
    default: false,
  },
  aiSummaryDisabled: {
    type: Boolean,
    default: false,
  },
});

defineEmits([
  "update-show-charts",
  "handle-save",
  "update-date-range",
  "custom-action",
  "ai-summary-click",
]);

const resetDateRange = () => {
  const dateRange = retrieveSessionDateRange();
  savedSearchStore.startDate = new Date(dateRange.start);
  savedSearchStore.endDate = new Date(dateRange.end);
};

const onPopoverClose = () => {
  if (savedSearchStore.dateRangeErrMsg !== "") {
    resetDateRange();
    savedSearchStore.dateRangeErrMsg = "";
  }
};
</script>

<style></style>
